import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useRouter } from "next/router";
import { X } from "lucide-react";
import ReactCountryFlag from "react-country-flag";
import { useEffect, useRef, useState } from "react";
import { normalizeLocale, t, SupportedLocale, PUBLIC_LOCALES, LOCALES, DEFAULT_LOCALE, getLocaleFromPath, isPublicLocale } from "@/lib/i18n";
import { persistLocalePreference } from "@/lib/localePreference";
import { WhereToBuySection } from "@/components/WhereToBuySection";
import { PushSubscribeButton } from "@/components/PushSubscribeButton";
import { AnalyticsBeacon } from "@/components/AnalyticsBeacon";
import { PromoBanner } from "@/components/PromoBanner";
import { getPublicPathFromAsPath, getPublicPathname, pathWithoutLocalePrefix, stripInternalRouteQuery } from "@/lib/publicPath";
import { getHomeUrl, getAbout12GoUrl, getWhy12GoUrl, getWhereToBuyUrl, getContactUrl, getAllProductsUrl, getCategoryUrl, getMilitarySubcategoryUrl, getProductsPathForLocale, isProductSlugPattern } from "@/lib/urls";
import layoutStyles from "@/styles/Layout.module.css";
type LayoutProps = {
  children: React.ReactNode;
};
const localeCountryCode: Record<SupportedLocale, string> = {
  en: "GB",
  de: "DE",
  es: "ES",
  fr: "FR",
  nl: "NL",
  pl: "PL",
  zh: "CN",
  it: "IT",
  ru: "RU"
};
function normalizeStaticPathForLocale(path: string, locale: SupportedLocale): string {
  const normalizedPath = path || "/";
  if (normalizedPath === "/about-12go") return pathWithoutLocalePrefix(getAbout12GoUrl(locale));
  if (normalizedPath === "/why-12go") return pathWithoutLocalePrefix(getWhy12GoUrl(locale));
  if (normalizedPath === "/where-to-buy-12go") return pathWithoutLocalePrefix(getWhereToBuyUrl(locale));
  if (normalizedPath === "/contact") return pathWithoutLocalePrefix(getContactUrl(locale));
  return normalizedPath;
}
export function Layout(t0) {
  const $ = _c(241);
  const {
    children
  } = t0;
  const router = useRouter();
  const {
    asPath
  } = router;
  const publicPath = getPublicPathFromAsPath(asPath || "/");
  const currentPath = getPublicPathname(asPath || "/");
  const locale = getLocaleFromPath(currentPath);
  const pathForNav = pathWithoutLocalePrefix(currentPath);
  const pathForNavWithLeading = pathForNav === "/" ? "" : pathForNav;
  const isNavActive = href => {
    const hrefPath = pathWithoutLocalePrefix(href);
    if (hrefPath === "/") {
      return pathForNav === "/";
    }
    return pathForNav === hrefPath || pathForNav.startsWith(hrefPath + "/");
  };
  let t1;
  if ($[0] !== asPath) {
    t1 = () => stripInternalRouteQuery((asPath || "").split("?")[1] || "");
    $[0] = asPath;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getPreservedQueryString = t1;
  const getLocaleHref = newLocale => {
    const preservedQuery = getPreservedQueryString();
    if (newLocale === locale) {
      return publicPath;
    }
    if (pathForNav.includes("[") || pathForNav.includes("]") || /%5b|%5d/i.test(pathForNav)) {
      return `${getHomeUrl(newLocale)}${preservedQuery}`;
    }
    const pathWithoutLocaleForSwitch = normalizeStaticPathForLocale(pathForNavWithLeading || "/", locale);
    const segments = pathWithoutLocaleForSwitch.replace(/^\/+/, "").split("/").filter(Boolean);
    if (segments.length === 1 && isProductSlugPattern(segments[0])) {
      return `/${newLocale}/${segments[0]}${preservedQuery}`;
    }
    const localizedPath = getProductsPathForLocale(segments, locale, newLocale);
    const fallback = pathWithoutLocaleForSwitch || "/";
    const targetPath = localizedPath ?? fallback;
    const targetWithLocale = newLocale === DEFAULT_LOCALE ? targetPath : targetPath.startsWith("/") && (LOCALES as readonly string[]).includes(targetPath.split("/")[1]) ? targetPath : `/${newLocale}${targetPath === "/" ? "" : targetPath}`;
    const targetWithQuery = `${targetWithLocale}${preservedQuery}`;
    return targetWithQuery;
  };
  const [isTopLocaleMenuOpen, setIsTopLocaleMenuOpen] = useState(false);
  const [isFooterLocaleMenuOpen, setIsFooterLocaleMenuOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const topLocaleMenuRef = useRef(null);
  const footerLocaleMenuRef = useRef(null);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsMobileNavOpen(false);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== asPath) {
    t3 = [asPath];
    $[3] = asPath;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  useEffect(() => {
    if (isPublicLocale(locale)) {
      persistLocalePreference(locale);
    }
  }, [locale]);
  const onLocaleSwitchClick = _temp;
  let t4;
  let t5;
  if ($[5] !== isMobileNavOpen) {
    t4 = () => {
      if (!isMobileNavOpen) {
        return;
      }
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    };
    t5 = [isMobileNavOpen];
    $[5] = isMobileNavOpen;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[8] !== isFooterLocaleMenuOpen || $[9] !== isMobileNavOpen || $[10] !== isTopLocaleMenuOpen) {
    t6 = () => {
      if (!isTopLocaleMenuOpen && !isFooterLocaleMenuOpen && !isMobileNavOpen) {
        return;
      }
      const onKeyDown = e => {
        if (e.key === "Escape") {
          setIsTopLocaleMenuOpen(false);
          setIsFooterLocaleMenuOpen(false);
          setIsMobileNavOpen(false);
        }
      };
      const onPointerDown = e_0 => {
        const target = e_0.target as Node;
        const topEl = topLocaleMenuRef.current;
        const footerEl = footerLocaleMenuRef.current;
        const inTop = topEl ? topEl.contains(target) : false;
        const inFooter = footerEl ? footerEl.contains(target) : false;
        if (!inTop) {
          setIsTopLocaleMenuOpen(false);
        }
        if (!inFooter) {
          setIsFooterLocaleMenuOpen(false);
        }
      };
      window.addEventListener("keydown", onKeyDown);
      window.addEventListener("pointerdown", onPointerDown);
      return () => {
        window.removeEventListener("keydown", onKeyDown);
        window.removeEventListener("pointerdown", onPointerDown);
      };
    };
    t7 = [isTopLocaleMenuOpen, isFooterLocaleMenuOpen, isMobileNavOpen];
    $[8] = isFooterLocaleMenuOpen;
    $[9] = isMobileNavOpen;
    $[10] = isTopLocaleMenuOpen;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  const localeFlag = _temp2;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <AnalyticsBeacon />;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== locale) {
    t9 = <PromoBanner locale={locale} />;
    $[14] = locale;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== locale) {
    t10 = t(locale, "topBar.languages");
    $[16] = locale;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10) {
    t11 = <span className={layoutStyles.topBarLanguagesLabel}>{t10}</span>;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setIsTopLocaleMenuOpen(_temp3);
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== locale) {
    t13 = t(locale, "nav.language");
    $[21] = locale;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const t14 = localeCountryCode[locale];
  let t15;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      width: "1em",
      height: "0.75em"
    };
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t14) {
    t16 = <ReactCountryFlag countryCode={t14} svg={true} style={t15} aria-hidden={true} />;
    $[24] = t14;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== locale) {
    t17 = locale.toUpperCase();
    $[26] = locale;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span className={layoutStyles.languageSelectChevron} aria-hidden={true}>▾</span>;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== isTopLocaleMenuOpen || $[30] !== t13 || $[31] !== t16 || $[32] !== t17) {
    t19 = <button type="button" className={layoutStyles.languageSelectTrigger} onClick={t12} aria-haspopup="menu" aria-expanded={isTopLocaleMenuOpen} aria-label={t13}>{t16}{t17}{t18}</button>;
    $[29] = isTopLocaleMenuOpen;
    $[30] = t13;
    $[31] = t16;
    $[32] = t17;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const t20 = isTopLocaleMenuOpen && <div className={layoutStyles.languageMenu} role="menu" aria-label={t(locale, "nav.language")}>{PUBLIC_LOCALES.map(loc_1 => loc_1 === locale ? <span key={loc_1} role="menuitem" className={layoutStyles.languageMenuItemActive} aria-current="page">{localeFlag(loc_1)}<span>{loc_1.toUpperCase()}</span></span> : <a key={loc_1} href={getLocaleHref(loc_1)} role="menuitem" className={layoutStyles.languageMenuItem} onClick={() => {
      onLocaleSwitchClick(loc_1);
      setIsTopLocaleMenuOpen(false);
    }}>{localeFlag(loc_1)}<span>{loc_1.toUpperCase()}</span></a>)}</div>;
  let t21;
  if ($[34] !== t19 || $[35] !== t20) {
    t21 = <div className={layoutStyles.languageSelectLabel} ref={topLocaleMenuRef}>{t19}{t20}</div>;
    $[34] = t19;
    $[35] = t20;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== locale) {
    t22 = getHomeUrl(locale);
    $[37] = locale;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== t22) {
    t23 = <a href={t22} className={layoutStyles.logoLink}>12GO Bricks</a>;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== locale) {
    t24 = t(locale, "nav.primary");
    $[41] = locale;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  const t25 = isNavActive(getHomeUrl(locale)) ? <span className={`${layoutStyles.navLink} ${layoutStyles.navLinkActive}`} aria-current="page">{t(locale, "nav.home")}</span> : <a href={getHomeUrl(locale)} className={layoutStyles.navLink}>{t(locale, "nav.home")}</a>;
  const t26 = isNavActive(getAllProductsUrl(locale)) ? <span className={`${layoutStyles.navLink} ${layoutStyles.navLinkActive}`} aria-current="page">{t(locale, "nav.products")}</span> : <Link href={getAllProductsUrl(locale)} className={layoutStyles.navLink}>{t(locale, "nav.products")}</Link>;
  const t27 = isNavActive(getAbout12GoUrl(locale)) ? <span className={`${layoutStyles.navLink} ${layoutStyles.navLinkActive}`} aria-current="page">{t(locale, "nav.aboutBrand")}</span> : <Link href={getAbout12GoUrl(locale)} className={layoutStyles.navLink}>{t(locale, "nav.aboutBrand")}</Link>;
  const t28 = isNavActive(getWhy12GoUrl(locale)) ? <span className={`${layoutStyles.navLink} ${layoutStyles.navLinkActive}`} aria-current="page">{t(locale, "footer.whyChoose")}</span> : <Link href={getWhy12GoUrl(locale)} className={layoutStyles.navLink}>{t(locale, "footer.whyChoose")}</Link>;
  const t29 = isNavActive(getWhereToBuyUrl(locale)) ? <span className={`${layoutStyles.navLink} ${layoutStyles.navLinkActive}`} aria-current="page">{t(locale, "footer.whereToBuy")}</span> : <Link href={getWhereToBuyUrl(locale)} className={layoutStyles.navLink}>{t(locale, "footer.whereToBuy")}</Link>;
  let t30;
  if ($[43] !== t24 || $[44] !== t25 || $[45] !== t26 || $[46] !== t27 || $[47] !== t28 || $[48] !== t29) {
    t30 = <nav className={layoutStyles.navMenu} aria-label={t24}>{t25}{t26}{t27}{t28}{t29}</nav>;
    $[43] = t24;
    $[44] = t25;
    $[45] = t26;
    $[46] = t27;
    $[47] = t28;
    $[48] = t29;
    $[49] = t30;
  } else {
    t30 = $[49];
  }
  let t31;
  if ($[50] !== locale) {
    t31 = <PushSubscribeButton locale={locale} />;
    $[50] = locale;
    $[51] = t31;
  } else {
    t31 = $[51];
  }
  let t32;
  if ($[52] !== locale) {
    t32 = t(locale, "products.filters.searchPlaceholder");
    $[52] = locale;
    $[53] = t32;
  } else {
    t32 = $[53];
  }
  let t33;
  if ($[54] !== locale) {
    t33 = t(locale, "nav.search");
    $[54] = locale;
    $[55] = t33;
  } else {
    t33 = $[55];
  }
  let t34;
  if ($[56] !== t32 || $[57] !== t33) {
    t34 = <input type="search" name="q" placeholder={t32} className={layoutStyles.searchInput} aria-label={t33} />;
    $[56] = t32;
    $[57] = t33;
    $[58] = t34;
  } else {
    t34 = $[58];
  }
  let t35;
  if ($[59] !== locale) {
    t35 = t(locale, "nav.search");
    $[59] = locale;
    $[60] = t35;
  } else {
    t35 = $[60];
  }
  let t36;
  if ($[61] !== t35) {
    t36 = <button type="submit" className={layoutStyles.searchBtn}>{t35}</button>;
    $[61] = t35;
    $[62] = t36;
  } else {
    t36 = $[62];
  }
  const t37 = isMobileNavOpen ? t(locale, "nav.closeMenu") : t(locale, "nav.openMenu");
  let t38;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = () => setIsMobileNavOpen(_temp4);
    $[63] = t38;
  } else {
    t38 = $[63];
  }
  const t39 = `${layoutStyles.hamburgerLine} ${isMobileNavOpen ? layoutStyles.hamburgerLineTopOpen : ""}`;
  let t40;
  if ($[64] !== t39) {
    t40 = <span className={t39} />;
    $[64] = t39;
    $[65] = t40;
  } else {
    t40 = $[65];
  }
  const t41 = `${layoutStyles.hamburgerLine} ${isMobileNavOpen ? layoutStyles.hamburgerLineMidOpen : ""}`;
  let t42;
  if ($[66] !== t41) {
    t42 = <span className={t41} />;
    $[66] = t41;
    $[67] = t42;
  } else {
    t42 = $[67];
  }
  const t43 = `${layoutStyles.hamburgerLine} ${isMobileNavOpen ? layoutStyles.hamburgerLineBottomOpen : ""}`;
  let t44;
  if ($[68] !== t43) {
    t44 = <span className={t43} />;
    $[68] = t43;
    $[69] = t44;
  } else {
    t44 = $[69];
  }
  let t45;
  if ($[70] !== t40 || $[71] !== t42 || $[72] !== t44) {
    t45 = <span className={layoutStyles.hamburgerBox} aria-hidden={true}>{t40}{t42}{t44}</span>;
    $[70] = t40;
    $[71] = t42;
    $[72] = t44;
    $[73] = t45;
  } else {
    t45 = $[73];
  }
  let t46;
  if ($[74] !== isMobileNavOpen || $[75] !== t37 || $[76] !== t45) {
    t46 = <button type="button" className={layoutStyles.hamburgerBtn} aria-label={t37} aria-expanded={isMobileNavOpen} aria-controls="mobile-nav" onClick={t38}>{t45}</button>;
    $[74] = isMobileNavOpen;
    $[75] = t37;
    $[76] = t45;
    $[77] = t46;
  } else {
    t46 = $[77];
  }
  let t47;
  if ($[78] !== isMobileNavOpen) {
    t47 = isMobileNavOpen ? <div className={layoutStyles.mobileNavOverlay} onClick={() => setIsMobileNavOpen(false)} aria-hidden={true} /> : null;
    $[78] = isMobileNavOpen;
    $[79] = t47;
  } else {
    t47 = $[79];
  }
  const t48 = `${layoutStyles.mobileNavPanel} ${isMobileNavOpen ? layoutStyles.mobileNavPanelOpen : ""}`;
  const t49 = !isMobileNavOpen;
  let t50;
  if ($[80] !== locale) {
    t50 = t(locale, "nav.primary");
    $[80] = locale;
    $[81] = t50;
  } else {
    t50 = $[81];
  }
  const t51 = !isMobileNavOpen;
  let t52;
  if ($[82] !== locale) {
    t52 = t(locale, "nav.closeMenu");
    $[82] = locale;
    $[83] = t52;
  } else {
    t52 = $[83];
  }
  let t53;
  let t54;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = () => setIsMobileNavOpen(false);
    t54 = <X size={22} strokeWidth={2.25} aria-hidden={true} />;
    $[84] = t53;
    $[85] = t54;
  } else {
    t53 = $[84];
    t54 = $[85];
  }
  let t55;
  if ($[86] !== t52) {
    t55 = <div className={layoutStyles.mobileNavHeader}><button type="button" className={layoutStyles.mobileNavCloseBtn} aria-label={t52} onClick={t53}>{t54}</button></div>;
    $[86] = t52;
    $[87] = t55;
  } else {
    t55 = $[87];
  }
  let t56;
  if ($[88] !== locale) {
    t56 = t(locale, "nav.primary");
    $[88] = locale;
    $[89] = t56;
  } else {
    t56 = $[89];
  }
  const t57 = isNavActive(getHomeUrl(locale)) ? <span className={`${layoutStyles.mobileNavLink} ${layoutStyles.mobileNavLinkActive}`} aria-current="page">{t(locale, "nav.home")}</span> : <a href={getHomeUrl(locale)} className={layoutStyles.mobileNavLink} onClick={() => setIsMobileNavOpen(false)}>{t(locale, "nav.home")}</a>;
  const t58 = isNavActive(getAllProductsUrl(locale)) ? <span className={`${layoutStyles.mobileNavLink} ${layoutStyles.mobileNavLinkActive}`} aria-current="page">{t(locale, "nav.products")}</span> : <Link href={getAllProductsUrl(locale)} className={layoutStyles.mobileNavLink} onClick={() => setIsMobileNavOpen(false)}>{t(locale, "nav.products")}</Link>;
  const t59 = isNavActive(getAbout12GoUrl(locale)) ? <span className={`${layoutStyles.mobileNavLink} ${layoutStyles.mobileNavLinkActive}`} aria-current="page">{t(locale, "nav.aboutBrand")}</span> : <Link href={getAbout12GoUrl(locale)} className={layoutStyles.mobileNavLink} onClick={() => setIsMobileNavOpen(false)}>{t(locale, "nav.aboutBrand")}</Link>;
  const t60 = isNavActive(getWhy12GoUrl(locale)) ? <span className={`${layoutStyles.mobileNavLink} ${layoutStyles.mobileNavLinkActive}`} aria-current="page">{t(locale, "footer.whyChoose")}</span> : <Link href={getWhy12GoUrl(locale)} className={layoutStyles.mobileNavLink} onClick={() => setIsMobileNavOpen(false)}>{t(locale, "footer.whyChoose")}</Link>;
  const t61 = isNavActive(getWhereToBuyUrl(locale)) ? <span className={`${layoutStyles.mobileNavLink} ${layoutStyles.mobileNavLinkActive}`} aria-current="page">{t(locale, "footer.whereToBuy")}</span> : <Link href={getWhereToBuyUrl(locale)} className={layoutStyles.mobileNavLink} onClick={() => setIsMobileNavOpen(false)}>{t(locale, "footer.whereToBuy")}</Link>;
  let t62;
  if ($[90] !== t56 || $[91] !== t57 || $[92] !== t58 || $[93] !== t59 || $[94] !== t60 || $[95] !== t61) {
    t62 = <nav className={layoutStyles.mobileNavLinks} aria-label={t56}>{t57}{t58}{t59}{t60}{t61}</nav>;
    $[90] = t56;
    $[91] = t57;
    $[92] = t58;
    $[93] = t59;
    $[94] = t60;
    $[95] = t61;
    $[96] = t62;
  } else {
    t62 = $[96];
  }
  let t63;
  if ($[97] !== isMobileNavOpen || $[98] !== t48 || $[99] !== t49 || $[100] !== t50 || $[101] !== t51 || $[102] !== t55 || $[103] !== t62) {
    t63 = <div id="mobile-nav" className={t48} role="dialog" aria-modal={isMobileNavOpen} aria-hidden={t49} aria-label={t50} inert={t51}>{t55}{t62}</div>;
    $[97] = isMobileNavOpen;
    $[98] = t48;
    $[99] = t49;
    $[100] = t50;
    $[101] = t51;
    $[102] = t55;
    $[103] = t62;
    $[104] = t63;
  } else {
    t63 = $[104];
  }
  let t64;
  if ($[105] !== locale) {
    t64 = t(locale, "footer.categories");
    $[105] = locale;
    $[106] = t64;
  } else {
    t64 = $[106];
  }
  let t65;
  if ($[107] !== t64) {
    t65 = <h3 className={layoutStyles.footerColumnTitle}>{t64}</h3>;
    $[107] = t64;
    $[108] = t65;
  } else {
    t65 = $[108];
  }
  let t66;
  if ($[109] !== locale) {
    t66 = getCategoryUrl("military", locale);
    $[109] = locale;
    $[110] = t66;
  } else {
    t66 = $[110];
  }
  let t67;
  if ($[111] !== locale) {
    t67 = t(locale, "category.military");
    $[111] = locale;
    $[112] = t67;
  } else {
    t67 = $[112];
  }
  let t68;
  if ($[113] !== t66 || $[114] !== t67) {
    t68 = <Link href={t66} className={layoutStyles.footerCategoryMain}>{t67}</Link>;
    $[113] = t66;
    $[114] = t67;
    $[115] = t68;
  } else {
    t68 = $[115];
  }
  let t69;
  if ($[116] !== locale) {
    t69 = getMilitarySubcategoryUrl("tanks_vehicles", locale);
    $[116] = locale;
    $[117] = t69;
  } else {
    t69 = $[117];
  }
  let t70;
  if ($[118] !== locale) {
    t70 = t(locale, "militarySub.tanks_vehicles");
    $[118] = locale;
    $[119] = t70;
  } else {
    t70 = $[119];
  }
  let t71;
  if ($[120] !== t69 || $[121] !== t70) {
    t71 = <li><Link href={t69}>{t70}</Link></li>;
    $[120] = t69;
    $[121] = t70;
    $[122] = t71;
  } else {
    t71 = $[122];
  }
  let t72;
  if ($[123] !== locale) {
    t72 = getMilitarySubcategoryUrl("ships", locale);
    $[123] = locale;
    $[124] = t72;
  } else {
    t72 = $[124];
  }
  let t73;
  if ($[125] !== locale) {
    t73 = t(locale, "militarySub.ships");
    $[125] = locale;
    $[126] = t73;
  } else {
    t73 = $[126];
  }
  let t74;
  if ($[127] !== t72 || $[128] !== t73) {
    t74 = <li><Link href={t72}>{t73}</Link></li>;
    $[127] = t72;
    $[128] = t73;
    $[129] = t74;
  } else {
    t74 = $[129];
  }
  let t75;
  if ($[130] !== locale) {
    t75 = getMilitarySubcategoryUrl("other", locale);
    $[130] = locale;
    $[131] = t75;
  } else {
    t75 = $[131];
  }
  let t76;
  if ($[132] !== locale) {
    t76 = t(locale, "militarySub.other");
    $[132] = locale;
    $[133] = t76;
  } else {
    t76 = $[133];
  }
  let t77;
  if ($[134] !== t75 || $[135] !== t76) {
    t77 = <li><Link href={t75}>{t76}</Link></li>;
    $[134] = t75;
    $[135] = t76;
    $[136] = t77;
  } else {
    t77 = $[136];
  }
  let t78;
  if ($[137] !== t71 || $[138] !== t74 || $[139] !== t77) {
    t78 = <ul className={layoutStyles.footerSubList}>{t71}{t74}{t77}</ul>;
    $[137] = t71;
    $[138] = t74;
    $[139] = t77;
    $[140] = t78;
  } else {
    t78 = $[140];
  }
  let t79;
  if ($[141] !== t68 || $[142] !== t78) {
    t79 = <li>{t68}{t78}</li>;
    $[141] = t68;
    $[142] = t78;
    $[143] = t79;
  } else {
    t79 = $[143];
  }
  let t80;
  if ($[144] !== locale) {
    t80 = getCategoryUrl("civilian", locale);
    $[144] = locale;
    $[145] = t80;
  } else {
    t80 = $[145];
  }
  let t81;
  if ($[146] !== locale) {
    t81 = t(locale, "category.civilian");
    $[146] = locale;
    $[147] = t81;
  } else {
    t81 = $[147];
  }
  let t82;
  if ($[148] !== t80 || $[149] !== t81) {
    t82 = <li><Link href={t80} className={layoutStyles.footerCategoryMain}>{t81}</Link></li>;
    $[148] = t80;
    $[149] = t81;
    $[150] = t82;
  } else {
    t82 = $[150];
  }
  let t83;
  if ($[151] !== t79 || $[152] !== t82) {
    t83 = <ul className={layoutStyles.footerCategoriesList}>{t79}{t82}</ul>;
    $[151] = t79;
    $[152] = t82;
    $[153] = t83;
  } else {
    t83 = $[153];
  }
  let t84;
  if ($[154] !== t65 || $[155] !== t83) {
    t84 = <div className={layoutStyles.footerCategoriesColumn}>{t65}{t83}</div>;
    $[154] = t65;
    $[155] = t83;
    $[156] = t84;
  } else {
    t84 = $[156];
  }
  let t85;
  if ($[157] !== locale) {
    t85 = t(locale, "footer.info");
    $[157] = locale;
    $[158] = t85;
  } else {
    t85 = $[158];
  }
  let t86;
  if ($[159] !== t85) {
    t86 = <h3 className={layoutStyles.footerColumnTitle}>{t85}</h3>;
    $[159] = t85;
    $[160] = t86;
  } else {
    t86 = $[160];
  }
  let t87;
  if ($[161] !== locale) {
    t87 = getAbout12GoUrl(locale);
    $[161] = locale;
    $[162] = t87;
  } else {
    t87 = $[162];
  }
  let t88;
  if ($[163] !== locale) {
    t88 = t(locale, "footer.about");
    $[163] = locale;
    $[164] = t88;
  } else {
    t88 = $[164];
  }
  let t89;
  if ($[165] !== t87 || $[166] !== t88) {
    t89 = <li><Link href={t87}>{t88}</Link></li>;
    $[165] = t87;
    $[166] = t88;
    $[167] = t89;
  } else {
    t89 = $[167];
  }
  let t90;
  if ($[168] !== locale) {
    t90 = getWhy12GoUrl(locale);
    $[168] = locale;
    $[169] = t90;
  } else {
    t90 = $[169];
  }
  let t91;
  if ($[170] !== locale) {
    t91 = t(locale, "footer.whyChoose");
    $[170] = locale;
    $[171] = t91;
  } else {
    t91 = $[171];
  }
  let t92;
  if ($[172] !== t90 || $[173] !== t91) {
    t92 = <li><Link href={t90}>{t91}</Link></li>;
    $[172] = t90;
    $[173] = t91;
    $[174] = t92;
  } else {
    t92 = $[174];
  }
  let t93;
  if ($[175] !== locale) {
    t93 = getWhereToBuyUrl(locale);
    $[175] = locale;
    $[176] = t93;
  } else {
    t93 = $[176];
  }
  let t94;
  if ($[177] !== locale) {
    t94 = t(locale, "footer.whereToBuy");
    $[177] = locale;
    $[178] = t94;
  } else {
    t94 = $[178];
  }
  let t95;
  if ($[179] !== t93 || $[180] !== t94) {
    t95 = <li><Link href={t93}>{t94}</Link></li>;
    $[179] = t93;
    $[180] = t94;
    $[181] = t95;
  } else {
    t95 = $[181];
  }
  let t96;
  if ($[182] !== locale) {
    t96 = getContactUrl(locale);
    $[182] = locale;
    $[183] = t96;
  } else {
    t96 = $[183];
  }
  let t97;
  if ($[184] !== locale) {
    t97 = t(locale, "footer.contact");
    $[184] = locale;
    $[185] = t97;
  } else {
    t97 = $[185];
  }
  let t98;
  if ($[186] !== t96 || $[187] !== t97) {
    t98 = <li><Link href={t96}>{t97}</Link></li>;
    $[186] = t96;
    $[187] = t97;
    $[188] = t98;
  } else {
    t98 = $[188];
  }
  let t99;
  if ($[189] !== t89 || $[190] !== t92 || $[191] !== t95 || $[192] !== t98) {
    t99 = <ul className={layoutStyles.footerColumnList}>{t89}{t92}{t95}{t98}</ul>;
    $[189] = t89;
    $[190] = t92;
    $[191] = t95;
    $[192] = t98;
    $[193] = t99;
  } else {
    t99 = $[193];
  }
  let t100;
  if ($[194] !== t86 || $[195] !== t99) {
    t100 = <div>{t86}{t99}</div>;
    $[194] = t86;
    $[195] = t99;
    $[196] = t100;
  } else {
    t100 = $[196];
  }
  let t101;
  if ($[197] !== locale) {
    t101 = t(locale, "footer.language");
    $[197] = locale;
    $[198] = t101;
  } else {
    t101 = $[198];
  }
  let t102;
  if ($[199] !== t101) {
    t102 = <h3 className={layoutStyles.footerColumnTitle}>{t101}</h3>;
    $[199] = t101;
    $[200] = t102;
  } else {
    t102 = $[200];
  }
  const t103 = layoutStyles;
  let t104;
  if ($[201] === Symbol.for("react.memo_cache_sentinel")) {
    t104 = () => setIsFooterLocaleMenuOpen(_temp5);
    $[201] = t104;
  } else {
    t104 = $[201];
  }
  let t105;
  if ($[202] !== locale) {
    t105 = t(locale, "nav.language");
    $[202] = locale;
    $[203] = t105;
  } else {
    t105 = $[203];
  }
  const t106 = localeCountryCode[locale];
  let t107;
  if ($[204] === Symbol.for("react.memo_cache_sentinel")) {
    t107 = {
      width: "1em",
      height: "0.75em"
    };
    $[204] = t107;
  } else {
    t107 = $[204];
  }
  let t108;
  if ($[205] !== t106) {
    t108 = <ReactCountryFlag countryCode={t106} svg={true} style={t107} aria-hidden={true} />;
    $[205] = t106;
    $[206] = t108;
  } else {
    t108 = $[206];
  }
  let t109;
  if ($[207] !== locale) {
    t109 = locale.toUpperCase();
    $[207] = locale;
    $[208] = t109;
  } else {
    t109 = $[208];
  }
  let t110;
  if ($[209] === Symbol.for("react.memo_cache_sentinel")) {
    t110 = <span className={layoutStyles.languageSelectChevron} aria-hidden={true}>▾</span>;
    $[209] = t110;
  } else {
    t110 = $[209];
  }
  let t111;
  if ($[210] !== isFooterLocaleMenuOpen || $[211] !== t105 || $[212] !== t108 || $[213] !== t109) {
    t111 = <button type="button" className={layoutStyles.footerLanguageSelectTrigger} onClick={t104} aria-haspopup="menu" aria-expanded={isFooterLocaleMenuOpen} aria-label={t105}>{t108}{t109}{t110}</button>;
    $[210] = isFooterLocaleMenuOpen;
    $[211] = t105;
    $[212] = t108;
    $[213] = t109;
    $[214] = t111;
  } else {
    t111 = $[214];
  }
  const t112 = isFooterLocaleMenuOpen && <div className={layoutStyles.footerLanguageMenu} role="menu" aria-label={t(locale, "nav.language")}>{PUBLIC_LOCALES.map(loc_3 => loc_3 === locale ? <span key={loc_3} role="menuitem" className={layoutStyles.languageMenuItemActive} aria-current="page">{localeFlag(loc_3)}<span>{loc_3.toUpperCase()}</span></span> : <a key={loc_3} href={getLocaleHref(loc_3)} role="menuitem" className={layoutStyles.languageMenuItem} onClick={() => {
      onLocaleSwitchClick(loc_3);
      setIsFooterLocaleMenuOpen(false);
    }}>{localeFlag(loc_3)}<span>{loc_3.toUpperCase()}</span></a>)}</div>;
  let t113;
  if ($[215] !== t103.footerLanguageSelectLabel || $[216] !== t111 || $[217] !== t112) {
    t113 = <div className={t103.footerLanguageSelectLabel} ref={footerLocaleMenuRef}>{t111}{t112}</div>;
    $[215] = t103.footerLanguageSelectLabel;
    $[216] = t111;
    $[217] = t112;
    $[218] = t113;
  } else {
    t113 = $[218];
  }
  let t114;
  if ($[219] === Symbol.for("react.memo_cache_sentinel")) {
    t114 = <hr className={layoutStyles.footerDivider} />;
    $[219] = t114;
  } else {
    t114 = $[219];
  }
  let t115;
  if ($[220] !== locale) {
    t115 = t(locale, "footer.copyright");
    $[220] = locale;
    $[221] = t115;
  } else {
    t115 = $[221];
  }
  let t116;
  if ($[222] !== t115) {
    t116 = <p>{t115}</p>;
    $[222] = t115;
    $[223] = t116;
  } else {
    t116 = $[223];
  }
  let t117;
  if ($[224] !== locale) {
    t117 = t(locale, "footer.disclaimer");
    $[224] = locale;
    $[225] = t117;
  } else {
    t117 = $[225];
  }
  let t118;
  if ($[226] !== t117) {
    t118 = <p>{t117}</p>;
    $[226] = t117;
    $[227] = t118;
  } else {
    t118 = $[227];
  }
  let t119;
  if ($[228] !== locale) {
    t119 = t(locale, "footer.affiliate");
    $[228] = locale;
    $[229] = t119;
  } else {
    t119 = $[229];
  }
  let t120;
  if ($[230] !== t119) {
    t120 = <p>{t119}</p>;
    $[230] = t119;
    $[231] = t120;
  } else {
    t120 = $[231];
  }
  let t121;
  if ($[232] !== locale) {
    t121 = t(locale, "footer.dataAccuracy");
    $[232] = locale;
    $[233] = t121;
  } else {
    t121 = $[233];
  }
  let t122;
  if ($[234] !== t121) {
    t122 = <p>{t121}</p>;
    $[234] = t121;
    $[235] = t122;
  } else {
    t122 = $[235];
  }
  let t123;
  if ($[236] !== t116 || $[237] !== t118 || $[238] !== t120 || $[239] !== t122) {
    t123 = <div className={layoutStyles.footerLegal}>{t116}{t118}{t120}{t122}</div>;
    $[236] = t116;
    $[237] = t118;
    $[238] = t120;
    $[239] = t122;
    $[240] = t123;
  } else {
    t123 = $[240];
  }
  return <div className={layoutStyles.wrapper}>{t8}{t9}<div className={layoutStyles.topBar}><div className={layoutStyles.languageSwitcher}>{t11}<div className={layoutStyles.languageButtonsRow}>{PUBLIC_LOCALES.map(loc_0 => loc_0 === locale ? <span key={loc_0} className={layoutStyles.languageButtonActive} aria-current="page">{localeFlag(loc_0)}{loc_0.toUpperCase()}</span> : <a key={loc_0} href={getLocaleHref(loc_0)} className={layoutStyles.languageButton} onClick={() => onLocaleSwitchClick(loc_0)}>{localeFlag(loc_0)}{loc_0.toUpperCase()}</a>)}</div>{t21}</div></div><header className={layoutStyles.navbar}>{t23}{t30}<div className={layoutStyles.navActions}>{t31}<form className={layoutStyles.searchForm} onSubmit={e_1 => {
          e_1.preventDefault();
          const q = (e_1.currentTarget.elements.namedItem("q") as HTMLInputElement)?.value?.trim();
          const base = getAllProductsUrl(locale);
          const url = q ? `${base}?q=${encodeURIComponent(q)}` : base;
          router.push(url);
        }}>{t34}{t36}</form>{t46}</div></header>{t47}{t63}{children}{!(asPath || "/").includes("where-to-buy-12go") && <WhereToBuySection locale={locale} />}<footer className={layoutStyles.footer}><div className={layoutStyles.footerGrid}>{t84}{t100}<div>{t102}<div className={layoutStyles.footerLanguageWrap}><div className={layoutStyles.footerLanguageButtonsRow}>{PUBLIC_LOCALES.map(loc_2 => loc_2 === locale ? <span key={loc_2} className={layoutStyles.footerLangActive} aria-current="true">{localeFlag(loc_2)}{loc_2.toUpperCase()}</span> : <a key={loc_2} href={getLocaleHref(loc_2)} className={layoutStyles.footerLangLink} onClick={() => onLocaleSwitchClick(loc_2)}>{localeFlag(loc_2)}{loc_2.toUpperCase()}</a>)}</div>{t113}</div></div></div>{t114}{t123}</footer></div>;
}
function _temp5(v_1) {
  return !v_1;
}
function _temp4(v_0) {
  return !v_0;
}
function _temp3(v) {
  return !v;
}
function _temp2(loc) {
  return <ReactCountryFlag countryCode={localeCountryCode[loc]} svg={true} style={{
    width: "1em",
    height: "0.75em"
  }} aria-hidden={true} />;
}
function _temp(newLocale_0) {
  if (isPublicLocale(newLocale_0)) {
    persistLocalePreference(newLocale_0);
  }
}